import React, { useState, useEffect } from 'react';
import { Mail, Eye, Save, RotateCcw, Code, FileText, Loader2, Check, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import RichTextEditor from '../RichTextEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EmailTemplatesEditor = () => {
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editedSubject, setEditedSubject] = useState('');
  const [editedHtml, setEditedHtml] = useState('');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('editor');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [hasChanges, setHasChanges] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchTemplates();
  }, []);

  useEffect(() => {
    if (selectedTemplate) {
      setHasChanges(
        editedSubject !== selectedTemplate.subject || 
        editedHtml !== selectedTemplate.html
      );
    }
  }, [editedSubject, editedHtml, selectedTemplate]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/email-templates/list`);
      setTemplates(res.data.templates || []);
      setCategories(['all', ...(res.data.categories || [])]);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTemplate = async (template) => {
    try {
      const res = await axios.get(`${API_URL}/api/email-templates/${template.template_key}`);
      setSelectedTemplate(res.data);
      setEditedSubject(res.data.subject);
      setEditedHtml(res.data.html);
      setHasChanges(false);
      
      // Load preview
      const previewRes = await axios.get(`${API_URL}/api/email-templates/${template.template_key}/preview`);
      setPreview(previewRes.data);
    } catch (error) {
      toast.error('Failed to load template');
    }
  };

  const handleSave = async () => {
    if (!selectedTemplate) return;
    
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/email-templates/admin/${selectedTemplate.template_key}`,
        { subject: editedSubject, html: editedHtml },
        { headers }
      );
      toast.success('Template saved successfully!');
      setHasChanges(false);
      
      // Refresh templates and preview
      fetchTemplates();
      const previewRes = await axios.get(`${API_URL}/api/email-templates/${selectedTemplate.template_key}/preview`);
      setPreview(previewRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!selectedTemplate) return;
    
    if (!window.confirm('Reset this template to default? Your customizations will be lost.')) return;
    
    try {
      await axios.post(
        `${API_URL}/api/email-templates/admin/${selectedTemplate.template_key}/reset`,
        {},
        { headers }
      );
      toast.success('Template reset to default');
      
      // Reload template
      handleSelectTemplate({ template_key: selectedTemplate.template_key });
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset template');
    }
  };

  const refreshPreview = async () => {
    if (!selectedTemplate) return;
    try {
      const res = await axios.post(`${API_URL}/api/email-templates/preview-render`, {
        subject: editedSubject,
        html: editedHtml,
        layout: selectedTemplate.layout || '',
      });
      setPreview({ ...preview, html: res.data.html, subject: res.data.subject });
    } catch (error) {
      setPreview({ ...preview, html: editedHtml, subject: editedSubject });
    }
  };

  useEffect(() => {
    if (selectedTemplate && activeTab === 'preview') {
      refreshPreview();
    }
  }, [activeTab, editedHtml, editedSubject]);

  const getCategoryColor = (cat) => {
    const colors = {
      support: 'bg-red-100 text-red-700',
      affiliate: 'bg-purple-100 text-purple-700',
      referral: 'bg-blue-100 text-blue-700',
      orders: 'bg-green-100 text-green-700',
      custom: 'bg-gray-100 text-gray-700'
    };
    return colors[cat] || colors.custom;
  };

  const filteredTemplates = selectedCategory === 'all' 
    ? templates 
    : templates.filter(t => t.category === selectedCategory);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Email Templates</h2>
          <p className="text-sm text-gray-500">Customize email templates sent to users and affiliates</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Template List */}
        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="mb-4">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
            >
              {categories.map(cat => (
                <option key={cat} value={cat}>
                  {cat === 'all' ? 'All Categories' : cat.charAt(0).toUpperCase() + cat.slice(1)}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-2 max-h-[600px] overflow-y-auto">
            {filteredTemplates.map((template) => (
              <div
                key={template.template_key}
                onClick={() => handleSelectTemplate(template)}
                className={`p-3 rounded-lg cursor-pointer transition-colors ${
                  selectedTemplate?.template_key === template.template_key
                    ? 'bg-blue-50 border-2 border-blue-200'
                    : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium text-sm">{template.name}</p>
                    <p className="text-xs text-gray-500 mt-1">{template.description}</p>
                  </div>
                  {!template.is_default && (
                    <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">
                      Customized
                    </span>
                  )}
                </div>
                <div className="mt-2">
                  <span className={`text-xs px-2 py-0.5 rounded ${getCategoryColor(template.category)}`}>
                    {template.category}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Editor/Preview */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm">
          {selectedTemplate ? (
            <>
              <div className="p-4 border-b flex items-center justify-between">
                <div>
                  <h3 className="font-semibold">{selectedTemplate.name}</h3>
                  <p className="text-sm text-gray-500">{selectedTemplate.description}</p>
                </div>
                <div className="flex items-center gap-2">
                  {hasChanges && (
                    <span className="text-xs text-amber-600 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" /> Unsaved changes
                    </span>
                  )}
                  {!selectedTemplate.is_default && (
                    <Button variant="outline" size="sm" onClick={handleReset}>
                      <RotateCcw className="h-4 w-4 mr-1" /> Reset
                    </Button>
                  )}
                  <Button 
                    size="sm" 
                    onClick={handleSave}
                    disabled={saving || !hasChanges}
                    className="bg-[#003456]"
                  >
                    {saving ? (
                      <><Loader2 className="h-4 w-4 animate-spin mr-1" /> Saving...</>
                    ) : (
                      <><Save className="h-4 w-4 mr-1" /> Save</>
                    )}
                  </Button>
                </div>
              </div>

              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="mx-4 mt-4">
                  <TabsTrigger value="editor" className="flex items-center gap-1">
                    <Code className="h-4 w-4" /> Editor
                  </TabsTrigger>
                  <TabsTrigger value="preview" className="flex items-center gap-1">
                    <Eye className="h-4 w-4" /> Preview
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="editor" className="p-4 space-y-4">
                  {/* Subject Line */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Subject Line
                    </label>
                    <Input
                      value={editedSubject}
                      onChange={(e) => setEditedSubject(e.target.value)}
                      placeholder="Email subject..."
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Use {`{{placeholder}}`} for dynamic content
                    </p>
                  </div>

                  {/* Placeholders Reference */}
                  {selectedTemplate.placeholders?.length > 0 && (
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-sm font-medium text-gray-700 mb-2">Available Placeholders</p>
                      <div className="flex flex-wrap gap-2">
                        {selectedTemplate.placeholders.map(p => (
                          <code 
                            key={p} 
                            className="text-xs bg-white px-2 py-1 rounded border cursor-pointer hover:bg-blue-50"
                            onClick={() => {
                              navigator.clipboard.writeText(`{{${p}}}`);
                              toast.success(`Copied {{${p}}}`);
                            }}
                          >
                            {`{{${p}}}`}
                          </code>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Body editor — rich text for LyphLine templates, raw HTML otherwise */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {selectedTemplate.layout === 'lyphline' ? 'Email Content' : 'Email HTML'}
                    </label>
                    {selectedTemplate.layout === 'lyphline' ? (
                      <>
                        <RichTextEditor
                          value={editedHtml}
                          onChange={setEditedHtml}
                          placeholder="Write the email content..."
                        />
                        <p className="text-xs text-gray-500 mt-2">
                          The LyphLine header and footer are added automatically. Type your message and drop in
                          placeholders like <code className="bg-gray-100 px-1 rounded">{`{{name}}`}</code> where the
                          customer's details should appear.
                        </p>
                      </>
                    ) : (
                      <textarea
                        value={editedHtml}
                        onChange={(e) => setEditedHtml(e.target.value)}
                        className="w-full h-[400px] px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm resize-none focus:ring-2 focus:ring-[#003456] focus:border-transparent"
                        placeholder="HTML content..."
                      />
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="preview" className="p-4">
                  {preview && (
                    <div>
                      <div className="bg-gray-100 rounded-lg p-3 mb-4">
                        <p className="text-sm text-gray-600">
                          <strong>Subject:</strong> {preview.subject}
                        </p>
                      </div>
                      <div 
                        className="border rounded-lg overflow-hidden"
                        style={{ height: '500px' }}
                      >
                        <iframe
                          srcDoc={preview.html}
                          title="Email Preview"
                          className="w-full h-full"
                          sandbox="allow-same-origin"
                        />
                      </div>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-[500px] text-gray-500">
              <Mail className="h-12 w-12 mb-4 text-gray-300" />
              <p>Select a template to edit</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailTemplatesEditor;
